import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronRight, FileText } from 'lucide-react';
import { usePage } from '@/hooks/useProducts';
import DocumentHead from '@/components/DocumentHead';
import QuickEditWrapper from '@/components/quickedit/QuickEditWrapper';
import PolicyDocument from '@/components/pages/PolicyDocument';
import { isPolicySlug } from '@/lib/policy-document';

export default function DynamicPage() {
  const { t } = useTranslation('pages');
  const { slug } = useParams<{ slug: string }>();
  const { data: page, isLoading, isError } = usePage(slug || '');

  if (isLoading) {
    return (
      <main className="mx-auto max-w-3xl px-4 py-8 sm:px-6 lg:px-8" aria-busy="true">
        <div className="animate-pulse space-y-6">
          <div className="h-4 w-48 rounded bg-stone-200" />
          <div className="h-10 rounded bg-stone-200 w-3/4" />
          <div className="space-y-3">
            <div className="h-4 rounded bg-stone-200" />
            <div className="h-4 rounded bg-stone-200" />
            <div className="h-4 rounded bg-stone-200 w-2/3" />
            <div className="h-4 rounded bg-stone-200" />
            <div className="h-4 rounded bg-stone-200 w-1/2" />
          </div>
        </div>
      </main>
    );
  }

  if (isError || !page) {
    return (
      <main className="mx-auto max-w-3xl px-4 py-16 sm:px-6 lg:px-8 text-center">
        <FileText className="mx-auto h-16 w-16 text-stone-300" aria-hidden="true" />
        <h1 className="mt-4 text-2xl font-bold text-stone-900">{t('notFound.heading')}</h1>
        <p className="mt-2 text-stone-600">
          {t('notFound.description')}
        </p>
        <Link
          to="/"
          className="mt-6 inline-flex items-center justify-center rounded-lg bg-sage-600 px-4 py-2 text-sm font-medium text-white hover:bg-sage-700 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-500 focus-visible:ring-offset-2"
        >
          {t('notFound.goHome')}
        </Link>
      </main>
    );
  }

  // Legal / policy pages are long, sectioned documents and get their own
  // layout (title band, contents list, anchored sections). Everything else —
  // About, Contact, and so on — keeps the plain article below.
  if (isPolicySlug(page.slug)) {
    return (
      // i18n-exempt: admin-only QuickEdit label, never customer-visible
      <QuickEditWrapper href={`/admin/pages/${page.id}`} label={`Edit page: ${page.title}`}>
        <DocumentHead
          title={page.title}
          description={page.meta_description}
          canonicalUrl={`/page/${page.slug}`}
        />
        <PolicyDocument
          title={page.title}
          slug={page.slug}
          html={page.content || page.body || ''}
          updatedAt={page.updated_at}
        />
      </QuickEditWrapper>
    );
  }

  return (
    // i18n-exempt: admin-only QuickEdit label, never customer-visible
    <QuickEditWrapper href={`/admin/pages/${page.id}`} label={`Edit page: ${page.title}`}>
    <main className="mx-auto max-w-3xl px-4 py-8 sm:px-6 lg:px-8">
      <DocumentHead
        title={page.title}
        description={page.meta_description}
        canonicalUrl={`/page/${page.slug}`}
      />
      {/* Breadcrumbs */}
      <nav aria-label={t('breadcrumbs.label')} className="mb-8">
        <ol className="flex items-center gap-1.5 text-sm text-stone-500">
          <li>
            <Link
              to="/"
              className="hover:text-sage-700 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-500 rounded"
            >
              {t('breadcrumbs.home')}
            </Link>
          </li>
          <li aria-hidden="true">
            <ChevronRight className="h-4 w-4" />
          </li>
          <li>
            <span className="font-medium text-stone-900" aria-current="page">
              {page.title}
            </span>
          </li>
        </ol>
      </nav>

      {/* Page title */}
      <h1 className="text-3xl font-bold text-stone-900 sm:text-4xl">
        {page.title}
      </h1>

      {/* Page content */}
      <article className="mt-8">
        <div
          className="prose prose-stone max-w-none prose-headings:font-semibold prose-a:text-sage-700 prose-img:rounded-lg"
          dangerouslySetInnerHTML={{ __html: page.content || page.body || '' }}
        />
      </article>
    </main>
    </QuickEditWrapper>
  );
}
